/**
 * SCR-008 — Courses list (admin + manager).
 * Table with search, level filter, status filter, pagination.
 * Create/edit via Dialog modal.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@client/components/data-table';
import { CourseForm, type CourseFormValues } from '@client/components/forms/course-form';
import { isApiError } from '@client/lib/queries/use-branches';
import {
  type CefrLevel,
  type Course,
  useCourses,
  useCreateCourse,
  useUpdateCourse,
} from '@client/lib/queries/use-courses';

export const Route = createFileRoute('/admin/courses/')({
  component: CoursesListPage,
});

const CEFR_LABELS: Record<string, string> = {
  pre_a1: 'Pre A1', a1: 'A1', a2: 'A2',
  b1: 'B1', b2: 'B2', c1: 'C1', c2: 'C2',
};

const CEFR_COLORS: Record<string, string> = {
  pre_a1: 'bg-fun-violet-100 text-fun-violet-700',
  a1: 'bg-brand-green-100 text-brand-green-700',
  a2: 'bg-brand-green-100 text-brand-green-700',
  b1: 'bg-brand-orange-100 text-brand-orange-700',
  b2: 'bg-brand-orange-100 text-brand-orange-700',
  c1: 'bg-destructive/10 text-destructive',
  c2: 'bg-destructive/10 text-destructive',
};

function CoursesListPage() {
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Course | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    level: levelFilter !== 'all' ? (levelFilter as CefrLevel) : undefined,
    isActive: activeFilter === 'all' ? undefined : activeFilter === 'true',
  };

  const { data, isLoading, isError, refetch } = useCourses(params);
  const createCourse = useCreateCourse();
  const updateCourse = useUpdateCourse(editTarget?.id ?? '');

  async function handleSubmit(values: CourseFormValues) {
    setFormError(null);
    try {
      const payload = {
        ...values,
        targetAgeMin: values.targetAgeMin !== '' ? Number(values.targetAgeMin) : undefined,
        targetAgeMax: values.targetAgeMax !== '' ? Number(values.targetAgeMax) : undefined,
        description: values.description || undefined,
      };
      if (editTarget) {
        await updateCourse.mutateAsync(payload);
        toast.success('Đã cập nhật khóa học.');
      } else {
        const created = await createCourse.mutateAsync(payload);
        toast.success('Đã tạo khóa học. Thêm chương + bài học để hoàn thiện giáo trình.');
        setDialogOpen(false);
        // Redirect to detail page with Units tab open so user can add curriculum immediately.
        // Use raw URL because the route doesn't define a search schema for ?tab=.
        if (created?.id) {
          window.location.href = `/admin/courses/${created.id}?tab=units`;
          return;
        }
      }
      setDialogOpen(false);
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setFormError('Mã khóa học đã tồn tại.');
      } else {
        setFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
      }
    }
  }

  const isSubmitting = createCourse.isPending || updateCourse.isPending;

  const columns: ColumnDef<Course>[] = [
    {
      accessorKey: 'code',
      header: 'Mã',
      cell: ({ row }) => (
        <Link
          to="/admin/courses/$courseId"
          params={{ courseId: row.original.id }}
          className="font-mono text-sm text-primary hover:underline"
        >
          {row.original.code}
        </Link>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Tên khóa học',
      cell: ({ row }) => (
        <Link
          to="/admin/courses/$courseId"
          params={{ courseId: row.original.id }}
          className="hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: 'level',
      header: 'Trình độ',
      cell: ({ row }) => (
        <Badge
          className={`${CEFR_COLORS[row.original.level] ?? ''} border-0 text-xs`}
        >
          {CEFR_LABELS[row.original.level] ?? row.original.level}
        </Badge>
      ),
    },
    {
      accessorKey: 'totalSessions',
      header: 'Buổi',
    },
    {
      accessorKey: 'sessionDurationMin',
      header: 'Thời lượng',
      cell: ({ row }) =>
        row.original.sessionDurationMin
          ? `${row.original.sessionDurationMin} phút`
          : '—',
    },
    {
      accessorKey: 'isActive',
      header: 'Trạng thái',
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge className="bg-brand-green-100 text-brand-green-700 border-0 text-xs">Đang mở</Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">Ngừng</Badge>
        ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                setEditTarget(row.original);
                setFormError(null);
                setDialogOpen(true);
              }}
            >
              <Pencil className="h-4 w-4 mr-2" /> Sửa
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-foreground">Khóa học</h1>
        <Button
          className="bg-accent text-accent-foreground hover:bg-accent/90 h-10 self-start sm:self-auto"
          onClick={() => { setEditTarget(null); setFormError(null); setDialogOpen(true); }}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Thêm khóa học
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage="Chưa có khóa học nào."
        searchPlaceholder="Tìm theo tên/mã..."
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        pagination={data ? { page, pageSize: PAGE_SIZE, total: data.total } : undefined}
        onPageChange={setPage}
        filterSlot={
          <div className="flex flex-wrap gap-2">
            <Select value={levelFilter} onValueChange={(v) => { setLevelFilter(v); setPage(1); }}>
              <SelectTrigger className="h-10 w-[130px]">
                <SelectValue placeholder="Trình độ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                <SelectItem value="pre_a1">Pre A1</SelectItem>
                <SelectItem value="a1">A1</SelectItem>
                <SelectItem value="a2">A2</SelectItem>
                <SelectItem value="b1">B1</SelectItem>
                <SelectItem value="b2">B2</SelectItem>
                <SelectItem value="c1">C1</SelectItem>
                <SelectItem value="c2">C2</SelectItem>
              </SelectContent>
            </Select>
            <Select value={activeFilter} onValueChange={(v) => { setActiveFilter(v); setPage(1); }}>
              <SelectTrigger className="h-10 w-[130px]">
                <SelectValue placeholder="Trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                <SelectItem value="true">Đang mở</SelectItem>
                <SelectItem value="false">Ngừng</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Sửa khóa học' : 'Thêm khóa học'}</DialogTitle>
          </DialogHeader>
          {formError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <CourseForm
            isEdit={!!editTarget}
            isSubmitting={isSubmitting}
            defaultValues={
              editTarget
                ? {
                    code: editTarget.code,
                    name: editTarget.name,
                    level: editTarget.level,
                    description: editTarget.description ?? '',
                    totalSessions: editTarget.totalSessions,
                    sessionDurationMin: editTarget.sessionDurationMin ?? 90,
                    targetAgeMin: editTarget.targetAgeMin ?? '',
                    targetAgeMax: editTarget.targetAgeMax ?? '',
                    isActive: editTarget.isActive,
                  }
                : undefined
            }
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
